import { useEffect, useState } from "react";
import api, { errMsg, fmtRM } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Database, Download, RefreshCw, ShieldCheck, AlertTriangle, Users, GraduationCap,
  CalendarClock, FolderTree, ArrowRight,
} from "lucide-react";

const PILL = "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold";

function Stat({ label, value, tone = "neutral", icon: Icon, testid }) {
  const t = tone === "danger" ? "border-[#D90429]/30 bg-[#D90429]/5 text-[#D90429]"
          : tone === "warn"   ? "border-amber-500/30 bg-amber-50 text-amber-700"
          : tone === "ok"     ? "border-emerald-500/30 bg-emerald-50/40 text-emerald-700"
          : "border-border bg-card text-foreground";
  return (
    <div className={`rounded-md border p-3 ${t}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-70">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className="text-2xl font-bold leading-tight">{value}</div>
    </div>
  );
}

export default function CoursesAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [backups, setBackups] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const { data: a } = await api.get("/admin/courses/audit");
      setData(a);
      const { data: b } = await api.get("/admin/maintenance/backups");
      setBackups(b || []);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function backup() {
    setBusy(true);
    try {
      const r = await api.post("/admin/maintenance/backup");
      toast.success(`Backup created: ${r.data.file} (${r.data.size_mb} MB)`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  if (loading || !data) return <div className="p-8"><Spinner /></div>;
  const s = data.summary;
  const issuesByFlag = (flag) => (data.courses || []).filter((c) => (c.flags || []).some((f) => f.startsWith(flag)));

  return (
    <div className="p-6 space-y-6" data-testid="courses-audit-page">
      <PageHeader title="Data Integrity Audit" subtitle="Read-only health check — courses ↔ teachers ↔ students ↔ schedules" />
      <div className="flex flex-wrap gap-2">
        <Button onClick={backup} disabled={busy} className="bg-secondary hover:bg-[#D96C52]" data-testid="backup-now-btn">
          <Database className="h-4 w-4 mr-1.5" />{busy ? "Backing up…" : "Backup database now"}
        </Button>
        <Button onClick={load} variant="outline" data-testid="refresh-audit-btn">
          <RefreshCw className="h-4 w-4 mr-1.5" />Refresh audit
        </Button>
      </div>

      {/* Headline counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Courses (active)" value={s.total_courses} icon={FolderTree} testid="stat-courses" />
        <Stat label="Teachers" value={s.total_teachers} icon={GraduationCap} testid="stat-teachers" />
        <Stat label="Students (active)" value={`${s.students_active}/${s.students_total}`} icon={Users} testid="stat-students" />
        <Stat label="Class schedules" value={`${s.schedules_active} active / ${s.schedules_needs_assignment} pending`} icon={CalendarClock} testid="stat-schedules" />
        <Stat label="Public-visible courses" value={s.public_visible} tone="ok" testid="stat-public" />
        <Stat label="With simplified names" value={s.with_simplified_name} tone="ok" testid="stat-simplified" />
        <Stat label="Courses w/ NO teacher" value={s.with_zero_teachers} tone={s.with_zero_teachers > 0 ? "warn" : "ok"} icon={AlertTriangle} testid="stat-no-teacher-courses" />
        <Stat label="Students w/ NO class" value={s.students_with_no_class} tone={s.students_with_no_class > 0 ? "warn" : "ok"} icon={AlertTriangle} testid="stat-no-class-students" />
      </div>

      {/* Dangling refs — high-risk */}
      {(s.schedules_with_dangling_course_id + s.schedules_with_dangling_teacher_id + s.schedules_with_dangling_student_id) > 0 && (
        <Card className="border-[#D90429]/40 bg-[#D90429]/5" data-testid="dangling-refs-card">
          <CardContent className="p-4 space-y-2">
            <h3 className="font-semibold flex items-center gap-2 text-[#D90429]">
              <AlertTriangle className="h-5 w-5" />Dangling references found in class schedules
            </h3>
            <p className="text-xs text-muted-foreground">
              These rows reference IDs that no longer exist in their parent collection.
              Likely cause: a teacher / student / course was deleted but the schedule wasn&apos;t re-pointed.
              Recommend backing up first, then reassigning or archiving these schedules.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <Stat label="Schedules → deleted COURSE" value={s.schedules_with_dangling_course_id} tone="danger" testid="dang-course" />
              <Stat label="Schedules → deleted TEACHER" value={s.schedules_with_dangling_teacher_id} tone="danger" testid="dang-teacher" />
              <Stat label="Schedules → deleted STUDENT" value={s.schedules_with_dangling_student_id} tone="danger" testid="dang-student" />
            </div>
            <div className="pt-2">
              <Button asChild className="bg-[#D90429] hover:bg-[#a8021f]" data-testid="open-reconcile-btn">
                <a href="/admin/dangling-refs">Reconcile orphan schedules <ArrowRight className="h-3.5 w-3.5 ml-1" /></a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="courses">
        <TabsList>
          <TabsTrigger value="courses" data-testid="tab-courses">Courses ({(data.courses || []).length})</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">Teachers ({(data.teachers || []).length})</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups">Backups ({backups.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="courses" className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Each row shows the linkage from a course → its teachers, active classes and pending (needs-schedule) classes.
            Flags surface structural issues you may want to fix before public launch.
          </p>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Course</TableHead>
                  <TableHead>Simplified</TableHead>
                  <TableHead className="text-right">Teachers</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                  <TableHead className="text-right">Needs schedule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.courses || []).slice().sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0)).map((c) => (
                  <TableRow key={c.id} data-testid={`audit-course-${c.id}`}>
                    <TableCell className="text-xs">{c.name}</TableCell>
                    <TableCell className="text-xs">{c.public_display_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right text-xs">{c.n_teachers}</TableCell>
                    <TableCell className="text-right text-xs">{c.n_active_schedules}</TableCell>
                    <TableCell className="text-right text-xs">{c.n_needs_schedule || ""}</TableCell>
                    <TableCell className="text-xs">
                      {c.public_visible ? <span className={`${PILL} bg-emerald-100 text-emerald-700`}>Public</span> : <span className={`${PILL} bg-muted text-muted-foreground`}>Hidden</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(c.flags || []).map((f) => (
                        <span key={f} className={`${PILL} mr-1 mb-1 ${f.includes("no_teacher") ? "bg-amber-100 text-amber-700" : f.includes("needs_schedule") ? "bg-blue-100 text-blue-700" : "bg-muted text-muted-foreground"}`}>{f}</span>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="teachers" className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Teachers with 0 courses can&apos;t be assigned to any new public booking. Dangling course_ids point to courses that have been deleted — clean them up via the existing Course form.
          </p>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Teacher</TableHead>
                  <TableHead className="text-right">Courses</TableHead>
                  <TableHead>Course names</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.teachers || []).slice().sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0)).map((t) => (
                  <TableRow key={t.id} data-testid={`audit-teacher-${t.id}`}>
                    <TableCell className="text-xs">{t.full_name}</TableCell>
                    <TableCell className="text-right text-xs">{t.n_courses}</TableCell>
                    <TableCell className="text-xs max-w-md">
                      {(t.course_names_known || []).slice(0, 5).join(", ")}{(t.course_names_known || []).length > 5 ? ` +${t.course_names_known.length - 5} more` : ""}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(t.flags || []).map((f) => (
                        <span key={f} className={`${PILL} mr-1 bg-amber-100 text-amber-700`}>{f}</span>
                      ))}
                      {(t.dangling_course_ids || []).length > 0 && <span className="text-[10px] text-muted-foreground italic ml-1">({t.dangling_course_ids.length} dangling ids)</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="backups" className="space-y-3">
          <p className="text-xs text-muted-foreground">Last {backups.length} database snapshots (newest first). Backups older than 10 are auto-pruned.</p>
          {backups.length === 0 ? (
            <p className="text-sm italic">No backups yet. Click <b>Backup database now</b> above to create one.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((b) => (
                  <TableRow key={b.file} data-testid={`backup-row-${b.file}`}>
                    <TableCell className="font-mono text-xs">{b.file}</TableCell>
                    <TableCell className="text-right text-xs">{b.size_mb} MB</TableCell>
                    <TableCell className="text-xs">{new Date(b.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
